import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Phone, Loader2, Car, Star, Check, MapPin } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import { str, num, safeRide, shortDate } from '../lib/safe';
import { BOOK_PATH } from '../lib/routes';
import { headline, pollDelay, makeProjector, pathFrom, fetchLive, startCall, nyTime, livePosition, isOpen } from '../lib/liveRide';
import { SignPreview } from './MeetGreetPanel';

const W = 640; const H = 360;

// The route drawn on a dark map (no map tiles: the page's security policy keeps third-party map scripts out): the path travelled, the pickup and drop-off pins, and the car.
export function LiveMap({ ride, route, tracking }) {
  const car = livePosition(tracking);
  const pickup = Number.isFinite(num(ride.pickup_lat, NaN)) && Number.isFinite(num(ride.pickup_lng, NaN)) ? { lat: num(ride.pickup_lat), lng: num(ride.pickup_lng) } : null;
  const drop = Number.isFinite(num(ride.dropoff_lat, NaN)) && Number.isFinite(num(ride.dropoff_lng, NaN)) ? { lat: num(ride.dropoff_lat), lng: num(ride.dropoff_lng) } : null;
  const trail = Array.isArray(route) ? route : [];
  const all = [...trail, pickup, drop, car].filter(Boolean);
  const { ok, project } = makeProjector(all, W, H);
  if (!ok) return <div data-testid="live-map" className="h-56 rounded-2xl border border-white/10 bg-white/[0.02] flex items-center justify-center text-sm text-gray-500">Map appears once your chauffeur is on the way.</div>;
  const d = pathFrom(trail, project);
  const pin = (pt, color, label, testid) => { const { x, y } = project(pt); return (
    <g key={testid} data-testid={testid} transform={`translate(${x.toFixed(1)} ${y.toFixed(1)})`}>
      <circle r="9" fill={color} fillOpacity="0.2" /><circle r="5" fill={color} /><text y="-14" textAnchor="middle" fontSize="11" fill="#e5e7eb">{label}</text>
    </g>); };
  const c = car ? project(car) : null;
  return (
    <div data-testid="live-map" className="rounded-2xl overflow-hidden border border-white/10" style={{ background: 'radial-gradient(120% 120% at 50% 0%, #17181d 0%, #0d0e11 70%)' }}>
      <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-auto block" role="img" aria-label="Live route map">
        <defs><pattern id="grid" width="40" height="40" patternUnits="userSpaceOnUse"><path d="M40 0H0V40" fill="none" stroke="#ffffff" strokeOpacity="0.04" /></pattern></defs>
        <rect width={W} height={H} fill="url(#grid)" />
        {d && <path d={d} fill="none" stroke="#F97316" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round" strokeOpacity="0.85" />}
        {pickup && pin(pickup, '#34d399', 'Pickup', 'map-pickup')}
        {drop && pin(drop, '#f9fafb', 'Drop-off', 'map-dropoff')}
        {c && (
          <g data-testid="map-car" transform={`translate(${c.x.toFixed(1)} ${c.y.toFixed(1)})`}>
            <circle r="16" fill="#F97316" fillOpacity="0.18"><animate attributeName="r" values="12;20;12" dur="2.4s" repeatCount="indefinite" /></circle>
            <circle r="7" fill="#F97316" stroke="#0d0e11" strokeWidth="2.5" />
          </g>
        )}
      </svg>
    </div>
  );
}

export function Timeline({ steps }) {
  const list = Array.isArray(steps) ? steps.filter((s) => s && typeof s === 'object') : [];
  return (
    <ol data-testid="live-timeline" className="space-y-0">
      {list.map((s, i) => {
        const done = s.state === 'done'; const cur = s.state === 'current';
        const at = nyTime(s.at);
        return (
          <li key={str(s.key) || i} data-state={s.state} className="flex gap-3">
            <div className="flex flex-col items-center">
              <span className={`h-5 w-5 rounded-full flex items-center justify-center border ${done ? 'bg-orange-500 border-orange-500' : cur ? 'border-orange-400 bg-orange-500/10' : 'border-white/15'}`}>
                {done ? <Check className="h-3 w-3 text-white" /> : cur ? <span className="h-2 w-2 rounded-full bg-orange-400 animate-pulse" /> : null}
              </span>
              {i < list.length - 1 && <span className={`w-px flex-1 min-h-[22px] ${done ? 'bg-orange-500/60' : 'bg-white/10'}`} />}
            </div>
            <div className="pb-4 -mt-0.5">
              <div className={`text-sm ${done || cur ? 'text-white' : 'text-gray-500'}`}>{str(s.label)}</div>
              {at && <div className="text-[11px] text-gray-500">{at}</div>}
            </div>
          </li>
        );
      })}
    </ol>
  );
}

export function ChauffeurCard({ ride, eta, canCall, onCall, calling }) {
  const d = ride.driver && typeof ride.driver === 'object' ? ride.driver : null;
  const name = str(ride.driverName) || (d ? str(d.name) : '');
  if (!name) return null;
  const v = (d && d.vehicle && typeof d.vehicle === 'object' ? d.vehicle : {}) || {};
  const label = str(v.label) || [str(v.color), str(v.make), str(v.model)].filter(Boolean).join(' ');
  const rating = d && Number.isFinite(num(d.rating, NaN)) && num(d.rating) > 0 ? num(d.rating) : null;
  const photo = d ? str(d.photoUrl) : '';
  return (
    <div data-testid="chauffeur-card" className="rounded-2xl border border-white/10 bg-white/[0.03] backdrop-blur p-4">
      <div className="flex items-center gap-3">
        {photo ? <img src={photo} alt="" className="h-14 w-14 rounded-full object-cover border border-white/10" />
          : <div className="h-14 w-14 rounded-full bg-orange-500/10 border border-orange-400/30 flex items-center justify-center text-orange-300 text-lg font-semibold">{name.slice(0, 1).toUpperCase()}</div>}
        <div className="min-w-0 flex-1">
          <div className="text-white font-medium truncate" data-testid="chauffeur-name">{name}</div>
          <div className="flex items-center gap-2 text-xs text-gray-400">
            {rating != null && <span className="flex items-center gap-0.5"><Star className="h-3 w-3 text-orange-400 fill-orange-400" />{rating.toFixed(2)}</span>}
            {eta != null && <span className="text-orange-300">{eta} min away</span>}
          </div>
        </div>
        {canCall && (
          <button type="button" data-testid="masked-call-btn" onClick={onCall} disabled={calling} aria-label="Call your chauffeur"
            className="h-11 w-11 rounded-full bg-gradient-to-r from-orange-500 to-orange-600 text-white flex items-center justify-center shadow-lg shadow-orange-900/30 disabled:opacity-50">
            {calling ? <Loader2 className="h-4 w-4 animate-spin" /> : <Phone className="h-4 w-4" />}
          </button>
        )}
      </div>
      {(label || str(v.plate || (d && d.plate))) && (
        <div className="mt-3 pt-3 border-t border-white/5 flex items-center justify-between text-sm">
          <span className="flex items-center gap-2 text-gray-300 min-w-0"><Car className="h-4 w-4 text-orange-400 shrink-0" /><span className="truncate">{label || 'Your vehicle'}</span></span>
          {str(v.plate || (d && d.plate)) && <span data-testid="vehicle-plate" className="font-mono text-xs tracking-widest text-white border border-white/15 rounded px-2 py-0.5">{str(v.plate || (d && d.plate))}</span>}
        </div>
      )}
      {d && str(d.tlcLicense) && <div className="mt-2 text-[11px] text-gray-500">TLC licence {str(d.tlcLicense)}</div>}
    </div>
  );
}

export default function LiveRide({ reference: refProp }) {
  const params = useParams();
  const reference = refProp || params.reference;
  const navigate = useNavigate();
  const { toast } = useToast();
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [notFound, setNotFound] = useState(false);
  const [calling, setCalling] = useState(false);
  const timer = useRef(null);
  const base = process.env.REACT_APP_BACKEND_URL;

  const load = useCallback(async () => {
    const token = (() => { try { return localStorage.getItem('rideburaq_token'); } catch (e) { return null; } })();
    let next = 3000;
    try {
      const r = await fetchLive(base, token, reference);
      if (r && r.notFound) { setNotFound(true); return; }
      setData(r); setError('');
      next = pollDelay(r && r.ride && r.ride.status, r && r.tracking);
    } catch (e) { setError(e.message || 'We could not refresh your ride.'); next = 10000; }
    if (next) timer.current = setTimeout(load, next);
  }, [base, reference]);

  useEffect(() => {
    load();
    const onVisible = () => { if (document.visibilityState === 'visible') { clearTimeout(timer.current); load(); } };
    document.addEventListener('visibilitychange', onVisible);
    return () => { clearTimeout(timer.current); document.removeEventListener('visibilitychange', onVisible); };
  }, [load]);

  const onCall = async () => {
    setCalling(true);
    try {
      const token = (() => { try { return localStorage.getItem('rideburaq_token'); } catch (e) { return null; } })();
      const r = await startCall(base, token, reference);
      toast({ title: 'Calling your chauffeur', description: str(r.message) || 'Your phone will ring first, then we connect you. Neither of you sees the other\'s number.', duration: 6000 });
    } catch (e) { toast({ title: 'Call unavailable', description: e.message, duration: 4000 }); }
    setCalling(false);
  };

  const shell = (children) => (
    <div className="min-h-screen text-white relative overflow-hidden" style={{ background: '#0D0E11' }}>
      <div aria-hidden="true" className="pointer-events-none absolute -top-40 -right-32 h-[420px] w-[420px] rounded-full" style={{ background: 'radial-gradient(circle, rgba(249,115,22,0.16), rgba(249,115,22,0) 70%)' }} />
      <div className="relative max-w-3xl mx-auto px-4 sm:px-6 py-6">
        <button type="button" data-testid="live-back" onClick={() => navigate('/rides')} className="flex items-center gap-1.5 text-sm text-gray-400 hover:text-orange-300 mb-5"><ArrowLeft className="h-4 w-4" /> My rides</button>
        {children}
      </div>
    </div>
  );

  if (notFound) return shell(<div data-testid="live-not-found" className="rounded-2xl border border-dashed border-white/10 px-6 py-14 text-center"><p className="text-gray-300 mb-4">We could not find that ride.</p><button type="button" onClick={() => navigate(BOOK_PATH)} className="text-sm font-medium text-orange-400">Book a ride →</button></div>);
  if (!data) return shell(<div data-testid="live-loading" className="py-24 text-center text-gray-400">{error ? <p>{error}</p> : <Loader2 className="h-7 w-7 animate-spin text-orange-400 mx-auto" />}</div>);

  const ride = safeRide(data.ride);
  const tracking = data.tracking && typeof data.tracking === 'object' ? data.tracking : null;
  const eta = tracking && !tracking.stale && Number.isFinite(num(tracking.etaMinutes, NaN)) ? Math.max(0, Math.round(num(tracking.etaMinutes))) : null;
  const head = headline(data.ride, tracking);
  const mg = data.ride && data.ride.meetGreet && typeof data.ride.meetGreet === 'object' ? data.ride.meetGreet : null;
  const cabin = data.ride && data.ride.cabin && typeof data.ride.cabin === 'object' ? data.ride.cabin : null;
  const cabinLine = cabin ? [str(cabin.tempLabel), str(cabin.conversation), str(cabin.music)].filter(Boolean).join(' · ') : '';
  const open = isOpen(ride.status);

  return shell(
    <div className="space-y-5">
      <div>
        <div className="text-[11px] tracking-[0.25em] uppercase text-orange-400/80">{str(ride.reference)}</div>
        <h1 data-testid="live-headline" className="text-2xl sm:text-3xl font-semibold mt-1">{head.title}</h1>
        <p data-testid="live-sub" className="text-gray-400 text-sm mt-1">{head.sub}</p>
        <div className="mt-4 h-px" style={{ background: 'linear-gradient(90deg, rgba(249,115,22,0.6), rgba(249,115,22,0))' }} />
      </div>
      {error && <div data-testid="live-error" className="text-xs text-amber-300">{error}</div>}
      {open && ride.status !== 'pending' && <LiveMap ride={data.ride || {}} route={data.route && data.route.points} tracking={tracking} />}
      {tracking && tracking.stale && <div data-testid="live-stale" className="text-xs text-gray-500">Waiting for your chauffeur's location…</div>}
      <ChauffeurCard ride={data.ride || {}} eta={eta} canCall={!!data.canCall} onCall={onCall} calling={calling} />
      <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-4 space-y-3">
        <div className="flex items-start gap-2 text-sm"><MapPin className="h-4 w-4 text-emerald-400 mt-0.5 shrink-0" /><span className="text-gray-300">{str(ride.pickupAddress)}</span></div>
        <div className="flex items-start gap-2 text-sm"><MapPin className="h-4 w-4 text-white mt-0.5 shrink-0" /><span className="text-gray-300">{str(ride.dropoffAddress)}</span></div>
        <div className="pt-2 border-t border-white/5 flex items-center justify-between text-[12px] text-gray-500">
          <span>{shortDate(ride.scheduledTime || ride.createdAt)}</span>
          {str(data.ride && data.ride.vehicleClassName) && <span className="text-orange-400/80">{str(data.ride.vehicleClassName)}</span>}
        </div>
        {cabinLine && <div data-testid="live-cabin" className="text-[12px] text-gray-400">{cabinLine}</div>}
      </div>
      {mg && (
        <div data-testid="live-meet-greet" className="rounded-2xl border border-white/10 bg-white/[0.03] p-4">
          <div className="text-[11px] tracking-[0.2em] uppercase text-gray-500 mb-1">Meet &amp; greet</div>
          <SignPreview name={str(mg.signName)} />
          {str(mg.meetingPoint) && <p data-testid="live-meeting-point" className="text-sm text-gray-300 mt-1">{str(mg.meetingPoint)}</p>}
        </div>
      )}
      <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-4">
        <div className="text-[11px] tracking-[0.2em] uppercase text-gray-500 mb-3">Status</div>
        <Timeline steps={data.timeline} />
      </div>
    </div>
  );
}
